import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';

// Default weights as defined in the backend
export const DEFAULT_WEIGHTS = {
  recent_reviews: 75,
  sentiment: 12,
  total_reviews: 8,
  traffic: 5,
};

// sessionStorage key for persisting custom weights (session-only)
const WEIGHTS_STORAGE_KEY = 'localytics_score_weights_session';

/**
 * Load saved weights from sessionStorage or return defaults
 */
const loadSavedWeights = () => {
  try {
    const saved = sessionStorage.getItem(WEIGHTS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate the saved weights
      const total = Object.values(parsed).reduce((sum, w) => sum + w, 0);
      if (total === 100 && Object.keys(DEFAULT_WEIGHTS).every(key => typeof parsed[key] === 'number')) {
        return parsed;
      }
    }
  } catch (e) {
    logger.error('Error loading saved weights:', e);
  }
  return DEFAULT_WEIGHTS;
};

/**
 * Save weights to sessionStorage
 */
const saveWeightsToStorage = (weights) => {
  try {
    sessionStorage.setItem(WEIGHTS_STORAGE_KEY, JSON.stringify(weights));
  } catch (e) {
    logger.error('Error saving weights:', e);
  }
};

/**
 * Recalculate Localytics scores based on custom weights
 * Uses the component scores from the API and applies new weights
 * @param {Array} originalResults - Original results from API with component scores
 * @param {Object} weights - Custom weights object
 * @returns {Array} Recalculated and sorted results
 */
export const recalculateScoresWithWeights = (originalResults, weights) => {
  if (!originalResults || originalResults.length === 0) return originalResults;

  // Calculate weighted scores for each business
  const businessScores = originalResults.map(business => {
    const components = business.components || {};
    
    // Get component scores (0-100 scale)
    const recentReviewsScore = components.recent_reviews_score || 0;
    const sentimentScore = components.sentiment_score || 0;
    const totalReviewsScore = components.total_reviews_score || 0;
    const trafficScore = components.traffic_score || 0;
    
    // Calculate weighted score (weights are percentages, so divide by 100)
    const weightedScore = (
      recentReviewsScore * (weights.recent_reviews / 100) +
      sentimentScore * (weights.sentiment / 100) +
      totalReviewsScore * (weights.total_reviews / 100) +
      trafficScore * (weights.traffic / 100)
    );
    
    return {
      ...business,
      original_localytics_score: business.original_localytics_score || business.localytics_score,
      weighted_score: weightedScore,
    };
  });

  // Normalize to 0-100 scale relative to portfolio
  const weightedScoreValues = businessScores.map(b => b.weighted_score);
  const minScore = Math.min(...weightedScoreValues);
  const maxScore = Math.max(...weightedScoreValues);
  const scoreRange = maxScore - minScore || 1;

  // Calculate normalized scores
  const normalizedResults = businessScores.map(business => {
    const normalizedScore = Math.round(((business.weighted_score - minScore) / scoreRange) * 100);
    return {
      ...business,
      localytics_score: Math.max(0, Math.min(100, normalizedScore)),
    };
  });

  // Sort by new score (descending)
  normalizedResults.sort((a, b) => b.localytics_score - a.localytics_score || b.weighted_score - a.weighted_score);

  // Ensure unique scores at top (break ties)
  for (let i = 1; i < normalizedResults.length; i++) {
    if (normalizedResults[i].localytics_score >= normalizedResults[i - 1].localytics_score) {
      normalizedResults[i].localytics_score = Math.max(0, normalizedResults[i - 1].localytics_score - 1);
    }
  }

  // Clean up internal weighted_score
  return normalizedResults.map(({ weighted_score, ...rest }) => rest);
};

/**
 * Calculate a single business score with custom weights
 * @param {Object} components - Component scores object
 * @param {Object} weights - Custom weights object
 * @returns {number} Calculated weighted score (0-100)
 */
export const calculateSingleBusinessScore = (components, weights) => {
  if (!components) return 0;
  
  const recentReviewsScore = components.recent_reviews_score || 0;
  const sentimentScore = components.sentiment_score || 0;
  const totalReviewsScore = components.total_reviews_score || 0;
  const trafficScore = components.traffic_score || 0;
  
  const weightedScore = (
    recentReviewsScore * (weights.recent_reviews / 100) +
    sentimentScore * (weights.sentiment / 100) +
    totalReviewsScore * (weights.total_reviews / 100) +
    trafficScore * (weights.traffic / 100)
  );
  
  return Math.round(weightedScore);
};

// Create the context
const WeightsContext = createContext(null);

/**
 * WeightsProvider - Provides custom weight state to all children
 */
export const WeightsProvider = ({ children }) => {
  // Initialize from sessionStorage
  const [weights, setWeights] = useState(loadSavedWeights);

  // Check if using custom weights
  const isUsingCustomWeights = useMemo(() => {
    return Object.keys(DEFAULT_WEIGHTS).some(
      key => weights[key] !== DEFAULT_WEIGHTS[key]
    );
  }, [weights]);

  // Update weights and save to sessionStorage
  const updateWeights = useCallback((newWeights) => {
    setWeights(newWeights);
    saveWeightsToStorage(newWeights);
    logger.log('Weights updated:', newWeights);
  }, []);

  // Reset to defaults
  const resetWeights = useCallback(() => {
    setWeights(DEFAULT_WEIGHTS);
    saveWeightsToStorage(DEFAULT_WEIGHTS);
    logger.log('Weights reset to defaults');
  }, []);

  const value = useMemo(() => ({
    weights,
    updateWeights,
    resetWeights,
    isUsingCustomWeights,
    DEFAULT_WEIGHTS,
    recalculateScoresWithWeights,
    calculateSingleBusinessScore,
  }), [weights, updateWeights, resetWeights, isUsingCustomWeights]);

  return (
    <WeightsContext.Provider value={value}>
      {children}
    </WeightsContext.Provider>
  );
};

/**
 * useWeights - Hook to access weight context
 */
export const useWeights = () => {
  const context = useContext(WeightsContext);
  if (!context) {
    throw new Error('useWeights must be used within a WeightsProvider');
  }
  return context;
};

export default WeightsContext;
